import { db, storage } from '../../../context/Firebase';
import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import BiodataRepository from '../../../domain/repositories/BiodataRepository';
import Biodata from '../../../domain/entities/Biodata';

class FirebaseBiodataRepository extends BiodataRepository {
    constructor() {
        super();
        this.collectionRef = collection(db, 'marriage_profiles');
    }

    /**
     * Converts a Firestore document snapshot to a Biodata entity.
     * @param {DocumentSnapshot} docSnap - The Firestore document snapshot.
     * @returns {Biodata|null}
     */
    _toBiodataEntity(docSnap) {
        const data = docSnap.data();

        // Handle both camelCase and space-separated field names from Firebase
        const fullName = data.fullName ?? data['Full Name'] ?? data.fullname;
        const gender = data.gender ?? data.Gender;

        // Validate required fields before creating entity
        if (!fullName || !gender) {
            console.warn(`Skipping document ${docSnap.id}: missing required fields (fullName or gender)`);
            return null;
        }

        // Handle dob - convert Firestore timestamp to Date, fallback to existing Date or null
        let dobValue = null;
        if (data.dob?.toDate) {
            dobValue = data.dob.toDate();
        } else if (data.Dob?.toDate) {
            dobValue = data.Dob.toDate();
        } else if (data.dob instanceof Date) {
            dobValue = data.dob;
        }

        // Handle createdAt - convert Firestore timestamp to Date, fallback to current date
        let createdAtValue = new Date();
        if (data.createdAt?.toDate) {
            createdAtValue = data.createdAt.toDate();
        } else if (data['Created At']?.toDate) {
            createdAtValue = data['Created At'].toDate();
        } else if (data.createdAt instanceof Date) {
            createdAtValue = data.createdAt;
        }

        return new Biodata({
            id: docSnap.id,
            fullName: fullName,
            ageRange: data.ageRange ?? data['Age Range'],
            caste: data.caste ?? data.Caste,
            height: data.height ?? data.Height,
            dob: dobValue,
            gender: gender,
            income: data.income ?? data.Income,
            qualification: data.qualification ?? data.Qualification,
            workplace: data.workplace ?? data.Workplace,
            maritalStatus: data.maritalStatus ?? data['Marital Status'],
            status: data.status ?? data.Status ?? 'Created',
            imagePaths: data.imagePaths ?? data.ImagePaths ?? [],
            createdAt: createdAtValue,
            createdBy: data.createdBy ?? data['Created By'] ?? null,
        });
    }

    async getBiodataProfiles() {
        try {
            // Try to order by createdAt first
            const q = query(this.collectionRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs
                .map(doc => this._toBiodataEntity(doc))
                .filter(biodata => biodata !== null); // Filter out invalid documents
        } catch (error) {
            // If ordering fails (e.g., missing createdAt field or index), fetch without ordering
            console.warn('Failed to order by createdAt, fetching all profiles without ordering:', error);
            const querySnapshot = await getDocs(this.collectionRef);
            return querySnapshot.docs
                .map(doc => this._toBiodataEntity(doc))
                .filter(biodata => biodata !== null); // Filter out invalid documents
        }
    }

    async getBiodataById(id) {
        const docRef = doc(db, 'marriage_profiles', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return this._toBiodataEntity(docSnap);
        }
        return null;
    }

    async createBiodata(biodataData) {
        const imagePaths = [];
        if (biodataData.imageFiles && biodataData.imageFiles.length > 0) {
            for (const file of biodataData.imageFiles) {
                const imageRef = ref(storage, `biodata_images/${Date.now()}-${file.name}`);
                const snapshot = await uploadBytes(imageRef, file);
                const downloadURL = await getDownloadURL(snapshot.ref);
                imagePaths.push(downloadURL);
            }
        }

        const newBiodataData = {
            fullName: biodataData.fullName,
            ageRange: biodataData.ageRange,
            caste: biodataData.caste,
            height: biodataData.height,
            dob: biodataData.dob,
            gender: biodataData.gender,
            income: biodataData.income,
            qualification: biodataData.qualification,
            workplace: biodataData.workplace,
            maritalStatus: biodataData.maritalStatus,
            status: biodataData.status || 'Created', // Default status
            imagePaths: imagePaths,
            createdAt: serverTimestamp(),
        };
        const docRef = await addDoc(this.collectionRef, newBiodataData);
        return new Biodata({ ...newBiodataData, id: docRef.id, createdAt: new Date() }); // createdAt will be a Date object after creation
    }

    async updateBiodata(id, updates) {
        const docRef = doc(db, 'marriage_profiles', id);
        const updatedData = { ...updates };

        // Handle image file updates if any
        if (updates.imageFiles && updates.imageFiles.length > 0) {
            const newImagePaths = [];
            for (const file of updates.imageFiles) {
                const imageRef = ref(storage, `biodata_images/${Date.now()}-${file.name}`);
                const snapshot = await uploadBytes(imageRef, file);
                const downloadURL = await getDownloadURL(snapshot.ref);
                newImagePaths.push(downloadURL);
            }
            updatedData.imagePaths = newImagePaths; // Overwrite or append, depending on desired behavior
            delete updatedData.imageFiles; // Remove imageFiles from updates before sending to Firestore
        } else if (updates.imageFiles === null) { // Explicitly remove images
            updatedData.imagePaths = [];
            delete updatedData.imageFiles;
        }

        await updateDoc(docRef, updatedData);
    }

    async deleteBiodata(id) {
        const docRef = doc(db, 'marriage_profiles', id);
        await deleteDoc(docRef);
    }

    async updateBiodataStatus(id, newStatus) {
        const docRef = doc(db, 'marriage_profiles', id);
        await updateDoc(docRef, { status: newStatus });
    }
}

export default FirebaseBiodataRepository;
